import {
  Anchor,
  Box,
  Button,
  Grid,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Title,
  Badge,
  Modal,
  Divider,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import moment from "moment";
import React, { useEffect, useState } from "react";
import InputField, { InputFieldTypes } from "../components/input_field";
import Table from "../components/table";
import { useLanguage } from "../contexts/language.context";
import { GET_ADMIN_WHITELABEL_DETAIL, GET_ADMIN_WHITELABEL_STATUSES } from "../services/companies";
import { FaEye, FaSync } from "react-icons/fa";

interface WhitelabelUser {
  id: string;
  name?: string;
  email?: string;
  status?: string | number;
  parentId?: string;
  nextPayment?: string;
  createdAt?: string;
  plan?: {
    id?: string;
    title?: string;
    price?: number;
    recurrenceType?: string;
  } | null;
  billing?: {
    subscriptionId?: string;
    intentId?: string;
  };
  active: boolean;
}

export default function WLUsersManagement() {
  const [wlUsers, setWlUsers] = useState<WhitelabelUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<WhitelabelUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<WhitelabelUser | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const { str } = useLanguage();

  const normalizeStatus = (
    status?: string | number,
    active?: boolean
  ): "active" | "inactive" | "past_due" | "canceled" | "trialing" | "unknown" => {
    if (typeof status === "number") {
      if (status === 1) return "active";
      if (status === 0) return "inactive";
    }

    if (typeof status === "string") {
      const normalized = status.trim().toLowerCase();
      if (["active", "trialing", "past_due", "canceled", "inactive"].includes(normalized)) {
        return normalized as "active" | "inactive" | "past_due" | "canceled" | "trialing";
      }
    }

    if (typeof active === "boolean") return active ? "active" : "inactive";
    return "unknown";
  };

  const formatStatusLabel = (status?: string | number, active?: boolean) => {
    const normalized = normalizeStatus(status, active);
    if (normalized === "past_due") return str("WHITELABEL_USERS.PAST_DUE");
    if (normalized === "trialing") return str("WHITELABEL_USERS.TRIALING");
    if (normalized === "canceled") return str("WHITELABEL_USERS.CANCELED");
    if (normalized === "unknown") return str("WHITELABEL_USERS.NOT_AVAILABLE");
    if (normalized === "active") return str("ACTIVE");
    if (normalized === "inactive") return str("INACTIVE");
    return str("WHITELABEL_USERS.NOT_AVAILABLE");
  };

  const loadWhitelabelUsers = async () => {
    try {
      setLoading(true);
      const companies = await GET_ADMIN_WHITELABEL_STATUSES();

      const usersWithBilling = (companies || []).map((item: any) => {
        const nextPayment = item?.company?.nextPayment;
        const activeFromDate = nextPayment
          ? moment(nextPayment).isAfter(moment())
          : false;

        return {
          id: item?.company?.id,
          name: item?.company?.name,
          email: item?.company?.email,
          status: item?.company?.status,
          parentId: item?.company?.parentId,
          nextPayment,
          createdAt: item?.company?.createdAt,
          plan: item?.plan || null,
          billing: item?.billing || {},
          active:
            typeof item?.active === "boolean" ? item.active : activeFromDate,
        };
      });

      setWlUsers(usersWithBilling);
      setFilteredUsers(usersWithBilling);
    } catch (err: any) {
      notifications.show({
        message: err.message || str("WHITELABEL_USERS.LOAD_ERROR"),
        color: "red",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    const filtered = wlUsers.filter(
      (user) =>
        user.name?.toLowerCase().includes(value.toLowerCase()) ||
        user.email?.toLowerCase().includes(value.toLowerCase()) ||
        String(user.status ?? "").toLowerCase().includes(value.toLowerCase()) ||
        formatStatusLabel(user.status, user.active).toLowerCase().includes(value.toLowerCase())
    );
    setFilteredUsers(filtered);
  };

  const handleViewDetails = async (user: WhitelabelUser) => {
    setSelectedUser(user);
    setDetailData(null);
    setLoadingDetails(true);
    try {
      const data = await GET_ADMIN_WHITELABEL_DETAIL(user.id);
      setDetailData(data);
    } catch (err: any) {
      notifications.show({
        message: err.message || str("WHITELABEL_USERS.LOAD_ERROR"),
        color: "red",
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    loadWhitelabelUsers();
  }, []);

  const getStatusBadgeColor = (status?: string | number, active?: boolean) => {
    switch (normalizeStatus(status, active)) {
      case "active":
        return "green";
      case "trialing":
        return "blue";
      case "past_due":
        return "yellow";
      case "canceled":
      case "inactive":
        return "red";
      default:
        return "gray";
    }
  };

  const columns = [
    {
      key: "name",
      title: str("NAME"),
      render: (item: WhitelabelUser) => (
        <Group gap="xs">
          <Box>
            <Text fw={500} size="sm">
              {item.name || str("WHITELABEL_USERS.NOT_AVAILABLE")}
            </Text>
            <Text size="xs" c="dimmed">
              {item.email}
            </Text>
          </Box>
        </Group>
      ),
    },
    {
      key: "status",
      title: str("STATUS"),
      render: (item: WhitelabelUser) => (
        <Group gap="xs">
          <Badge color={item.active ? "green" : "red"} variant="outline">
            {item.active
              ? str("WHITELABEL_USERS.VALID")
              : str("WHITELABEL_USERS.EXPIRED")}
          </Badge>
        </Group>
      ),
    },
    {
      key: "nextPayment",
      title: str("COMPANIES.NEXT_PAYMENT"),
      render: (item: WhitelabelUser) => (
        <Text size="sm">
          {item.nextPayment
            ? moment(item.nextPayment).format(str("DATE_FORMAT"))
            : str("WHITELABEL_USERS.NOT_AVAILABLE")}
        </Text>
      ),
    },
    {
      key: "billing",
      title: str("WHITELABEL_USERS.CURRENT_PLAN"),
      render: (item: WhitelabelUser) => (
        <Text size="sm">
          {item.plan?.title || str("WHITELABEL_USERS.NO_ACTIVE_PLAN")}
        </Text>
      ),
    },
  ];

  const actions = [
    {
      title: str("WHITELABEL_USERS.VIEW_DETAILS"),
      leftSection: <FaEye />,
      onClick: ({ item }: { item: WhitelabelUser; index: number }) =>
        handleViewDetails(item),
    },
  ];

  return (
    <>
      <Paper shadow="xs" p="md" mb="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>{str("WHITELABEL_USERS.TITLE")}</Title>
          <Button
            leftSection={<FaSync />}
            onClick={loadWhitelabelUsers}
            loading={loading}
            variant="light"
          >
            {str("WHITELABEL_USERS.REFRESH")}
          </Button>
        </Group>

        <InputField
          placeholder={str("WHITELABEL_USERS.SEARCH_PLACEHOLDER")}
          name="search"
          value={searchTerm}
          onChange={({ search }) => handleSearchChange(search)}
          fieldType={InputFieldTypes.STRING}
        />
      </Paper>

      <Paper shadow="xs" p="md">
        <Table
          data={filteredUsers}
          columns={columns}
          actions={actions}
          loading={loading}
        />
      </Paper>

      <Modal
        opened={!!selectedUser}
        onClose={() => {
          setSelectedUser(null);
          setDetailData(null);
        }}
        title={`${str("WHITELABEL_USERS.USER_DETAILS")}: ${selectedUser?.name || ""}`}
        size="xl"
      >
        {loadingDetails ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : detailData ? (
          <ScrollArea>
            <Stack gap="md">
              <div>
                <Title order={4}>{str("WHITELABEL_USERS.COMPANY_INFORMATION")}</Title>
                <Grid mt="md">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.COMPANY_NAME")}</Text>
                    <Text size="sm">{detailData.company?.name || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("EMAIL")}</Text>
                    <Text size="sm">{detailData.company?.email || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.COMPANY_ID")}</Text>
                    <Text size="sm">{detailData.company?.id || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.PARENT_ID")}</Text>
                    <Text size="sm">{detailData.company?.parentId || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                  </Grid.Col>
                </Grid>
              </div>

              <Divider />

              <div>
                <Title order={4}>{str("WHITELABEL_USERS.SUBSCRIPTION_INFORMATION")}</Title>
                <Grid mt="md">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>
                      {str("STATUS")}
                    </Text>
                  <Group gap="xs" mt="xs">
                    <Badge color={getStatusBadgeColor(detailData.company?.status, detailData.active)}>
                      {formatStatusLabel(detailData.company?.status, detailData.active)}
                    </Badge>
                    <Badge color={detailData.active ? "green" : "red"}>
                      {detailData.active
                        ? str("WHITELABEL_USERS.VALID")
                        : str("WHITELABEL_USERS.EXPIRED")}
                    </Badge>
                  </Group>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CURRENT_PLAN")}</Text>
                    <Text size="sm" mt="xs">
                      {detailData.plan?.title || str("WHITELABEL_USERS.NO_ACTIVE_PLAN")}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.NEXT_PAYMENT_DATE")}</Text>
                    <Text size="sm" mt="xs">
                      {detailData.company?.nextPayment
                        ? moment(detailData.company.nextPayment).format("DD/MM/YYYY HH:mm")
                        : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CREATED_AT")}</Text>
                    <Text size="sm" mt="xs">
                      {detailData.company?.createdAt
                        ? moment(detailData.company.createdAt).format("DD/MM/YYYY HH:mm")
                        : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                    </Text>
                  </Grid.Col>
                </Grid>
              </div>

              <Divider />

              <div>
                <Title order={4}>{str("WHITELABEL_USERS.STRIPE_BILLING")}</Title>
                <Grid mt="md">
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.SUBSCRIPTION_ID")}</Text>
                    <Text size="sm" mt="xs">
                      {detailData.billing?.subscriptionId || str("WHITELABEL_USERS.NOT_AVAILABLE")}
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <Text size="sm" fw={500}>{str("WHITELABEL_USERS.INTENT_ID")}</Text>
                    <Text size="sm" mt="xs">
                      {detailData.billing?.intentId || str("WHITELABEL_USERS.NOT_AVAILABLE")}
                    </Text>
                  </Grid.Col>
                </Grid>
              </div>

              {detailData.stripe && (
                <>
                  <Divider />
                  <div>
                    <Title order={4}>{str("WHITELABEL_USERS.STRIPE_SUBSCRIPTION")}</Title>
                    <Grid mt="md">
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Text size="sm" fw={500}>{str("STATUS")}</Text>
                        <Badge mt="xs" color={getStatusBadgeColor(detailData.stripe.status, detailData.active)}>
                          {formatStatusLabel(detailData.stripe.status, detailData.active)}
                        </Badge>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CUSTOMER_ID")}</Text>
                        <Text size="sm" mt="xs">{detailData.stripe.customerId || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CURRENT_PERIOD_START")}</Text>
                        <Text size="sm" mt="xs">
                          {detailData.stripe.currentPeriodStart
                            ? moment(detailData.stripe.currentPeriodStart).format("DD/MM/YYYY HH:mm")
                            : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                        </Text>
                      </Grid.Col>
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CURRENT_PERIOD_END")}</Text>
                        <Text size="sm" mt="xs">
                          {detailData.stripe.currentPeriodEnd
                            ? moment(detailData.stripe.currentPeriodEnd).format("DD/MM/YYYY HH:mm")
                            : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                        </Text>
                      </Grid.Col>
                      {detailData.stripe.cancelAt && (
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CANCEL_AT")}</Text>
                          <Text size="sm" mt="xs">{moment(detailData.stripe.cancelAt).format("DD/MM/YYYY HH:mm")}</Text>
                        </Grid.Col>
                      )}
                      {detailData.stripe.canceledAt && (
                        <Grid.Col span={{ base: 12, sm: 6 }}>
                          <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CANCELED_AT")}</Text>
                          <Text size="sm" mt="xs">{moment(detailData.stripe.canceledAt).format("DD/MM/YYYY HH:mm")}</Text>
                        </Grid.Col>
                      )}
                      <Grid.Col span={{ base: 12, sm: 6 }}>
                        <Text size="sm" fw={500}>{str("WHITELABEL_USERS.CANCEL_AT_PERIOD_END")}</Text>
                        <Text size="sm" mt="xs">
                          {detailData.stripe.cancelAtPeriodEnd ? str("WHITELABEL_USERS.YES") : str("WHITELABEL_USERS.NO")}
                        </Text>
                      </Grid.Col>
                    </Grid>
                  </div>
                </>
              )}

              {detailData.processedPayments?.stripe?.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <Title order={4}>{str("WHITELABEL_USERS.PAYMENT_HISTORY")}</Title>
                    <Table
                      mt="md"
                      data={detailData.processedPayments.stripe}
                      columns={[
                        {
                          key: "createdAt",
                          title: str("DATE"),
                          render: (inv: any) => (
                            <Text size="sm">
                              {inv.createdAt
                                ? moment(inv.createdAt).format(str("DATE_FORMAT"))
                                : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                            </Text>
                          ),
                        },
                        {
                          key: "number",
                          title: str("WHITELABEL_USERS.INVOICE_NUMBER"),
                          render: (inv: any) => (
                            <Text size="sm">{inv.number || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                          ),
                        },
                        {
                          key: "amountPaid",
                          title: str("WHITELABEL_USERS.AMOUNT_PAID"),
                          render: (inv: any) => (
                            <Text size="sm">
                              {typeof inv.amountPaid === "number"
                                ? `${(inv.amountPaid / 100).toFixed(2)} ${(inv.currency || "").toUpperCase()}`
                                : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                            </Text>
                          ),
                        },
                        {
                          key: "status",
                          title: str("STATUS"),
                          render: (inv: any) => (
                            <Badge color={inv.paid ? "green" : "red"} variant="outline">
                              {inv.status || str("WHITELABEL_USERS.NOT_AVAILABLE")}
                            </Badge>
                          ),
                        },
                        {
                          key: "links",
                          title: str("WHITELABEL_USERS.LINKS"),
                          render: (inv: any) => (
                            <Group gap="xs">
                              {inv.invoicePdf && (
                                <Anchor href={inv.invoicePdf} target="_blank" size="sm">
                                  {str("WHITELABEL_USERS.PDF")}
                                </Anchor>
                              )}
                              {inv.hostedInvoiceUrl && (
                                <Anchor href={inv.hostedInvoiceUrl} target="_blank" size="sm">
                                  {str("WHITELABEL_USERS.HOSTED_INVOICE")}
                                </Anchor>
                              )}
                            </Group>
                          ),
                        },
                      ]}
                    />
                  </div>
                </>
              )}

              {detailData.processedPayments?.internal?.length > 0 && (
                <>
                  <Divider />
                  <div>
                    <Title order={4}>{str("WHITELABEL_USERS.INTERNAL_PAYMENTS")}</Title>
                    <Table
                      mt="md"
                      data={detailData.processedPayments.internal}
                      columns={[
                        {
                          key: "paid_at",
                          title: str("WHITELABEL_USERS.PAID_AT"),
                          render: (pay: any) => (
                            <Text size="sm">
                              {pay.paid_at
                                ? moment(pay.paid_at).format(str("DATE_FORMAT"))
                                : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                            </Text>
                          ),
                        },
                        {
                          key: "origin",
                          title: str("WHITELABEL_USERS.ORIGIN"),
                          render: (pay: any) => (
                            <Text size="sm">{pay.origin || str("WHITELABEL_USERS.NOT_AVAILABLE")}</Text>
                          ),
                        },
                        {
                          key: "value",
                          title: str("WHITELABEL_USERS.AMOUNT_PAID"),
                          render: (pay: any) => (
                            <Text size="sm">
                              {typeof pay.value === "number"
                                ? `${pay.value.toFixed(2)} ${(pay.currency || "").toUpperCase()}`
                                : str("WHITELABEL_USERS.NOT_AVAILABLE")}
                            </Text>
                          ),
                        },
                      ]}
                    />
                  </div>
                </>
              )}
            </Stack>
          </ScrollArea>
        ) : null}
      </Modal>
    </>
  );
}
